/**
 * AnimatedLogo — the Builder lockup with a deliberately restrained
 * "premium calm" motion behavior.
 *
 * Motion rationale
 * ─────────────────
 * In a product that lives inside a user's day (RDO updates, medição
 * approvals, document expiry alerts), the logo should feel like a
 * steady anchor, not a party trick. The entrance is barely long enough
 * to register — 250ms, opacity 0→1, scale 0.95→1 on a soft OutQuint
 * curve — so it settles into place rather than arriving. There's no
 * translation, no overshoot, no rotation, no spring. Nothing that would
 * pull the eye.
 *
 * The idle state is an opacity breathe from 1.0 → 0.92 → 1.0 over ten
 * seconds on a sine-like ease. At that speed and that delta it reads,
 * if at all, as "the surface is breathing" — not as an animation. It is
 * intentionally just above the threshold of perception.
 *
 * Every `repeatMs` ticks (defaults to the hero variant's 30s cadence on
 * auth/home pages), the entrance replays. That turns the logo into a
 * slow metronome — a quiet reassurance on a page the user may be
 * staring at for a minute while they wait for an invite or paste a
 * token. It is *not* applied on the authed TopBar where the user is
 * working; there, the lockup plays its entrance once on mount and then
 * just breathes.
 *
 * Engineering
 * ───────────
 *   - WAAPI (el.animate) instead of CSS keyframes so repeats can
 *     cancel/retarget cleanly and so the animations run off the main
 *     thread (they're hardware-accelerated by the compositor the same
 *     way CSS transitions on opacity/transform are).
 *   - `prefers-reduced-motion: reduce` short-circuits everything:
 *     final state, no entrance, no breathing, no repeat. The user gets
 *     a static logo.
 *   - No external libraries — ~90 LOC of React + 0 dependencies.
 *   - Commits final transform/opacity after the entrance so that if the
 *     idle animation is briefly paused (tab backgrounded, browser
 *     throttling) the logo never snaps back to the pre-entrance state.
 *
 * Tweak notes
 * ───────────
 * Everything that affects the aesthetic is a named constant near the
 * top of this file. Change *one* at a time; if a knob noticeably
 * changes the feel, it's probably already pushed too far.
 *
 *   ENTRANCE_DURATION_MS  —  keep in 180–320ms. Below 180 it flashes;
 *                            above 320 it starts to feel like a
 *                            "reveal" which undercuts the restraint.
 *   PREMIUM_EASE          —  OutQuint is the default. For even more
 *                            restraint try cubic-bezier(0.19,1,0.22,1)
 *                            (OutExpo). Do NOT use an ease-in or any
 *                            curve that ends past 1,1 (overshoot).
 *   IDLE_MIN_OPACITY      —  0.92 is the floor. Raise toward 0.94 for
 *                            "almost invisible"; drop no lower than
 *                            0.88 or the breathing becomes a pulse.
 *   IDLE_PERIOD_MS        —  10s. Shorter feels anxious; longer than
 *                            14s is indistinguishable from static.
 *   repeatMs (prop)       —  30000 on hero pages. Omit to run the
 *                            entrance once on mount. Don't go below
 *                            15s — the page will feel twitchy.
 *
 * Err on the side of restraint. If it catches your eye, it's loud.
 */
import { useEffect, useRef } from 'react';
import { Logo } from './Logo';

/** Soft, polished OutQuint — lands without punch. */
const PREMIUM_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const ENTRANCE_DURATION_MS = 250;
const IDLE_PERIOD_MS = 10_000;
const IDLE_MIN_OPACITY = 0.92;

export interface AnimatedLogoProps extends React.ComponentProps<typeof Logo> {
  /**
   * If set, the entrance animation replays on this interval (ms). Omit
   * to run the entrance exactly once on mount.
   *
   * Hero/home pages pass `30_000`. The authed TopBar passes nothing.
   */
  repeatMs?: number;
}

export function AnimatedLogo({ repeatMs, ...logoProps }: AnimatedLogoProps) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect the user's OS setting. We intentionally fall through to a
    // fully static lockup rather than "reduced" motion — at this size
    // any motion is too much if reduce is on.
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      return;
    }

    let entranceAnim: Animation | null = null;
    let idleAnim: Animation | null = null;
    let disposed = false;

    const startIdle = () => {
      if (disposed) return;
      idleAnim?.cancel();
      idleAnim = el.animate(
        [
          { opacity: 1 },
          { opacity: IDLE_MIN_OPACITY },
          { opacity: 1 },
        ],
        {
          duration: IDLE_PERIOD_MS,
          iterations: Infinity,
          easing: 'ease-in-out',
        },
      );
    };

    const runEntrance = () => {
      if (disposed) return;
      idleAnim?.cancel();
      entranceAnim?.cancel();
      entranceAnim = el.animate(
        [
          { opacity: 0, transform: 'scale(0.95)' },
          { opacity: 1, transform: 'scale(1)' },
        ],
        {
          duration: ENTRANCE_DURATION_MS,
          easing: PREMIUM_EASE,
          fill: 'both',
        },
      );
      entranceAnim.finished
        .then(() => {
          if (disposed) return;
          // Commit the end state so idle can take over without a snap.
          entranceAnim?.cancel();
          el.style.opacity = '1';
          el.style.transform = 'scale(1)';
          startIdle();
        })
        .catch(() => {
          // A rapid re-trigger cancels the previous entrance — that's
          // expected; the new entrance will chain its own idle.
        });
    };

    runEntrance();

    let intervalId: number | undefined;
    if (repeatMs && repeatMs > 0) {
      intervalId = window.setInterval(runEntrance, repeatMs);
    }

    return () => {
      disposed = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
      entranceAnim?.cancel();
      idleAnim?.cancel();
    };
  }, [repeatMs]);

  // inline-flex so the wrapper collapses to the Logo's own box — the
  // caller's layout (hero center, TopBar left) is untouched.
  return (
    <span ref={ref} className="inline-flex">
      <Logo {...logoProps} />
    </span>
  );
}
